import React, { useEffect, useState } from "react";
import axios from "axios";
import { getPipelines, addStage } from "../../api/kanbanApi";
import { FiPlus } from "react-icons/fi";
import useToast from "../../hooks/useToast";
import Toast from "../../components/Toast";

export default function PipelinePermissions() {
  const { toast, success, error, hideToast } = useToast();
  const [pipeline, setPipeline] = useState(null);
  const [stages, setStages] = useState([]);
  const [addStageName, setAddStageName] = useState("");
  const [addStageTargetType, setAddStageTargetType] = useState("single");
  const [loading, setLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [gmailStatus, setGmailStatus] = useState(null); // null=unknown, true=connected, false=disconnected

  const storedUserData = JSON.parse(localStorage.getItem("userData") || "{}");
  const userRole = storedUserData?.role || "member";
  const isManager = userRole === "manager" || userRole === "admin";
  const token = localStorage.getItem("authToken");
  const currentUserId = localStorage.getItem("userId");

  // Check Gmail connection status on mount
  useEffect(() => {
    const checkGmailStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/gmail/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const connected = res.data?.connected ?? res.data?.isConnected ?? false;
        setGmailStatus(connected);
        if (!connected) localStorage.setItem("gmailDisconnected", "true");
        else localStorage.removeItem("gmailDisconnected");
      } catch (e) {
        // If API doesn't exist yet, fall back to localStorage flag set by socket event
        const flagged = localStorage.getItem("gmailDisconnected") === "true";
        setGmailStatus(flagged ? false : null);
      }
    };
    checkGmailStatus();
  }, [token]);

  const handleGmailReconnect = () => {
    const url = process.env.REACT_APP_API_BASE_URL + "/gmail/login";
    window.location.href = url;
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/invite/get-team-member`,
        { headers: { Authorization: `Bearer ${token}` }, params: { page: 1, limit: 100 } }
      );
      setTeamMembers(res.data?.data?.invites || []);
    } catch (e) {
      console.error("Failed to load team members", e);
    }
  };

  const fetchPipeline = async () => {
    try {
      const res = await getPipelines(undefined, {});
      setPipeline({ _id: res.data.pipelineId });
      setStages(
        res.data.stages.map((s) => ({
          id: String(s._id || s.stageId),
          name: s.name,
          order: s.order,
          dealCount: (s.deals || []).length,
        }))
      );
    } catch (e) {
      console.error("Failed to load pipeline", e);
    }
  };

  useEffect(() => {
    fetchPipeline();
    fetchTeamMembers();
  }, []);

  const handleAddStage = async (e) => {
    e.preventDefault();
    if (!pipeline || !addStageName.trim()) return;
    setLoading(true);
    try {
      const payload = {
        name: addStageName.trim(),
        order: stages.length + 1,
        targetType: addStageTargetType,
      };
      if (addStageTargetType === "group") {
        const orgId = localStorage.getItem("orgId");
        if (orgId && orgId !== "null") payload.orgId = orgId;
      } else if (addStageTargetType.startsWith("member_")) {
        payload.targetUserId = addStageTargetType.replace("member_", "");
      } else {
        payload.targetUserId = localStorage.getItem("userId");
      }
      const data = await addStage(pipeline._id, payload);
      if (data.status_code === 201 || data.status_code === 200) {
        success("Stage created successfully", { title: "Stage" });
      }
      setAddStageName("");
      setAddStageTargetType("single");
      fetchPipeline();
    } catch (e) {
      error(e?.response?.data?.message || "Failed to create stage", { title: "Stage" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 600, paddingTop: 0 }}>
      <h3 style={{ fontWeight: 600, fontSize: "1.6rem", color: "#151d3b", marginBottom: 4 }}>
        Pipeline Stages
      </h3>
      <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: 24 }}>
        Manage pipeline stages for your organization. Only managers and admins can add stages.
      </p>

      {/* Existing stages */}
      <div style={{ marginBottom: 28 }}>
        {stages.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: "0.9rem" }}>No stages found.</div>
        ) : (
          stages.map((stage, idx) => (
            <div
              key={stage.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 16px",
                marginBottom: 8,
                background: "#f8f9ff",
                border: "1px solid #e3e6f0",
                borderRadius: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{
                  width: 24, height: 24, borderRadius: "50%",
                  background: "var(--primary-color)", color: "#fff",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "0.75rem", fontWeight: 700, flexShrink: 0,
                }}>
                  {idx + 1}
                </span>
                <span style={{ fontWeight: 600, color: "#151d3b", fontSize: "0.95rem" }}>
                  {stage.name}
                </span>
              </div>
              <span style={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                {stage.dealCount} deal{stage.dealCount !== 1 ? "s" : ""}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Add stage form — managers/admins only */}
      {isManager && (
        <div style={{
          padding: 20,
          border: "1.5px dashed #d1d5db",
          borderRadius: 10,
          background: "#fafafa",
        }}>
          <h4 style={{ margin: "0 0 14px 0", fontWeight: 600, color: "#374151", fontSize: "0.95rem" }}>
            Add New Stage
          </h4>
          <form onSubmit={handleAddStage} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ display: "block", fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>
                Stage Name
              </label>
              <input
                required
                value={addStageName}
                onChange={(e) => setAddStageName(e.target.value)}
                placeholder="e.g. Negotiation"
                style={{
                  width: "100%",
                  padding: "9px 14px",
                  borderRadius: 8,
                  border: "1.5px solid #e3e6f0",
                  fontSize: "0.95rem",
                  outline: "none",
                  background: "#fff",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ minWidth: 160 }}>
              <label style={{ display: "block", fontSize: "0.82rem", color: "#6b7280", marginBottom: 4 }}>
                Visibility
              </label>
              <select
                value={addStageTargetType}
                onChange={(e) => setAddStageTargetType(e.target.value)}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 8,
                  border: "1.5px solid #e3e6f0",
                  fontSize: "0.95rem",
                  outline: "none",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                <option value="single">
                  {storedUserData?.name || storedUserData?.email || "Myself"}
                </option>
                {teamMembers.map((member) => (
                  <option key={member._id} value={`member_${member._id}`}>
                    {member.name || member.email}
                  </option>
                ))}
                <option value="group">Organization</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={loading}
              style={{
                background: "var(--primary-color)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "9px 20px",
                fontWeight: 600,
                fontSize: "0.95rem",
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                opacity: loading ? 0.7 : 1,
                height: 40,
              }}
            >
              <FiPlus /> {loading ? "Adding..." : "Add Stage"}
            </button>
          </form>
        </div>
      )}

      {!isManager && (
        <div style={{
          padding: 16, borderRadius: 8,
          background: "#fff7ed", border: "1px solid #fed7aa",
          color: "#92400e", fontSize: "0.9rem",
        }}>
          Only managers and admins can add pipeline stages.
        </div>
      )}

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        title={toast.title}
        duration={toast.duration}
        onClose={hideToast}
        position={toast.position}
      />

      {/* Gmail Connection Status — always visible */}
      <div style={{ marginTop: 40 }}>
        <h3 style={{ fontWeight: 600, fontSize: "1.6rem", color: "#151d3b", marginBottom: 4 }}>
          Gmail Connection
        </h3>
        <p style={{ color: "#6b7280", fontSize: "0.9rem", marginBottom: 20 }}>
          Connect your Gmail account to enable email sync and inbox features.
        </p>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          border: gmailStatus === false
            ? "1.5px solid #fca5a5"
            : gmailStatus === true
              ? "1.5px solid #86efac"
              : "1.5px solid #e3e6f0",
          borderRadius: 10,
          background: gmailStatus === false ? "#fff7f7" : gmailStatus === true ? "#f0fdf4" : "#fafafa",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Gmail icon */}
            <svg width="28" height="28" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="48" height="48" rx="8" fill="#fff"/>
              <path d="M8 14l16 11L40 14" stroke="#EA4335" strokeWidth="2.5" strokeLinejoin="round"/>
              <rect x="8" y="14" width="32" height="22" rx="2" stroke="#EA4335" strokeWidth="2.5" fill="none"/>
            </svg>
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#151d3b" }}>Gmail</div>
              <div style={{ fontSize: "0.82rem", color: gmailStatus === false ? "#dc2626" : gmailStatus === true ? "#16a34a" : "#6b7280", marginTop: 2 }}>
                {gmailStatus === false
                  ? "Disconnected — token expired"
                  : gmailStatus === true
                    ? "Connected"
                    : "Status unknown"}
              </div>
            </div>
          </div>
          <button
            onClick={handleGmailReconnect}
            style={{
              background: "var(--primary-color, #1976d2)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "9px 20px",
              fontWeight: 600,
              fontSize: "0.9rem",
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            {gmailStatus === true ? "Reconnect" : "Connect Gmail"}
          </button>
        </div>
      </div>
    </div>
  );
}
